// typescript
import React, {useState} from "react";
import { AjaxThoughtRestControllerApi, ThoughtInConversationDTO } from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import {Bookmark, Check, CircleSlash, Eye, Flag, History, MessageCircle, MoreHorizontal, Pencil, Pin, PinOff, Share, Sparkles, ArrowBigDown, ArrowBigUp, Trash2, X} from "lucide-react";
import {CtaButton, CtaButtonDropdown, CtaToggle, DropdownItem, VoteContainer} from "@/main/react/buttons/CtaButton";
import {Localizer} from "@/main/react/Types";
import {toast} from "sonner";
import {shareUrl} from "@/main/util/PlatformIdentifierUtil";
import logger from "@/lib/logger";
import {trackThoughtVote, trackBookmarkAdded, trackBookmarkRemoved, trackShare, trackThoughtPinned} from "@/main/analytics/analyticsEvents";
import {showReportEntityDialog} from "@/main/react/Dialog/ReportEntityDialog";
import {showYesNoDialog} from "@/main/react/Dialog/YesNoDialog";
import {
    ACCOUNT_ACCESS_DIALOG_COPY,
    buildWriteThoughtContinuation,
    withAccountAccessSurface,
} from "@/main/react/account_access/AccountAccessDialogContext";
import {useIsMobile} from "@/hooks/use-mobile";
import {getSafeToastMessageFromError} from "@/main/common/ApiUtils";
import type {QuestionStateView} from "@/main/common/QuestionFacetTypes";
import {thoughtPermalinkHref} from "@/main/react/thoughts/thoughtChainRoute";
import {applyVoteToState, type DiscussionVoteState} from "@/main/common/DiscussionVoteState";
import {
    rememberViewerActionRecord,
    resolveViewerActionRecord,
    useViewerActionRecord,
} from "@/main/common/ViewerActionRecords";
import {buildUserSafetyDropdownItems, useUserSafetyActions} from "@/main/react/user/useUserSafetyActions";
import {EndorsementControl} from "@/main/react/thoughts/EndorsementControl";
import type {ThoughtEndorsementSummary} from "@/main/common/EndorsementTypes";

type Props = {
    argumentPublicId: string;
    initialAgreementsCount?: number;
    initialDisagreementsCount?: number;
    initialBookmarksCount?: number;
    initiallyAgreed?: boolean;
    initiallyDisagreed?: boolean;
    initiallyBookmarked?: boolean;
    isOpeningArgument?: boolean;
    isAuthorTheLoggedInUser: boolean;
    // The thought's author, for the overflow menu's block / mute pair (design 2026-08-10, slice A). Prop
    // plumbing only: both parent displayers already have it, because both render the author's name. It is
    // optional so an un-threaded mount degrades to no safety controls rather than to a control that acts on
    // the wrong person — `useUserSafetyActions` treats a blank username as "offer nothing".
    authorUsername?: string | null;
    conversationPublicId?: string | null;
    discussionSlug: string;
    // Title of the post this thought replies to, for the share payload only (BL-083). Optional and
    // threaded from the page down, so an un-threaded mount keeps the pre-2026-08-07 payload shape
    // rather than sending an empty title — the chokepoint falls back to `shareUrl`'s positional
    // argument when `entityTitle` is absent.
    hostPostTitle?: string;
    localizer: Localizer;
    displayConversationButton?: boolean;
    isDeleted?: boolean;
    deletedAt?: string | null;
    canDelete?: boolean;
    canStartConversation?: boolean;
    // --- Editorial pin (designs/01-now/2026-07-27-pinned-thought-in-discussion-design.md). ---
    // `canPin` is a property of the DISCUSSION's authorship, not this thought's: it is true only for
    // the person who published the post, and only while that post is live and unarchived. `pinned` is
    // this thought's own state.
    pinned?: boolean;
    canPin?: boolean;
    // Public id of the thought currently occupying the discussion's single pin slot, if any. When it
    // names a DIFFERENT thought, pinning this one shows a confirm first — the current pin may be far
    // down the list and out of sight, so replacing it silently would be an invisible destructive side
    // effect on content the author cannot see.
    pinnedThoughtPublicId?: string | null;
    // Fired after a successful pin/unpin so the owning list can re-fetch and re-sort. The SERVER owns
    // the ordering; hand-moving the item locally would disagree with it on the next page load.
    onPinChanged?: () => void;
    // --- QUESTION facet (designs/01-now/2026-07-29-post-facets-question-facet-design.md §11.1). ---
    // `isQuestionAnswer` says this thought is an answer to a question, which is what makes the Accept
    // control meaningful at all. `canAccept` is the ASKER's permission and is therefore false for every
    // other reader — that is precisely what lets the control live on the card rather than in a menu
    // (Q-D9), because for everyone else it simply is not there.
    //
    // `accepted` is NOT local state here: the card owns it, because the card also draws the badge from it
    // and two copies of one boolean in a parent/child pair is how they drift. This component reports the
    // server's answer upward through `onAcceptedChanged` and re-renders from the prop.
    //
    // `discussionPublicId` is required for the write: the verb is addressed to the discussion, with the
    // answer as an argument to it, not to the answer as a resource of its own.
    discussionPublicId?: string;
    isQuestionAnswer?: boolean;
    accepted?: boolean;
    canAccept?: boolean;
    onAcceptedChanged?: (nextAccepted: boolean, view: QuestionStateView) => void;
    // --- CHANGED MY MIND (designs/01-now/2026-08-08-changed-my-mind-implementation-record.md). ---
    // `canMarkMindChanged` is the POST AUTHOR's permission, not this thought's property — false for
    // every other reader, which is exactly what lets the control sit on the card rather than in an
    // overflow menu nobody opens. It is the same shape as `canAccept` directly above.
    //
    // `mindChanged` is NOT local state here: the CARD owns it, because the card also draws the badge
    // from it, and two copies of one boolean in a parent/child pair is how they drift (rule 31's
    // "delete one of the copies", one layer up). This component neither writes nor guesses — it
    // reports the click and re-renders from the prop. The write itself lives with the composer on the
    // card, because the sentence is the feature and the mark is only its receipt.
    mindChanged?: boolean;
    canMarkMindChanged?: boolean;
    onMarkMindChangedClicked?: () => void;
    // --- ENDORSEMENTS (designs/00-inbox/2026-08-25-profile-endorsements-and-the-appreciation-wall-design.md
    // §9.1, §7.15). The DTO's summary for the chip, or null/undefined when the projection did not
    // resolve it (the reply tree's producer does not yet). Unlike the two pairs above, NOTHING about the
    // endorsement is owned by the card: `EndorsementControl` owns the control, the chip and the
    // composer for one thought as one record, because this row has three hosts and the act is offered
    // to every reader on every host. This component only decides whether to mount it at all.
    endorsements?: ThoughtEndorsementSummary | null;
    // Public id of the conversation currently open in the discussion rail (desktop). When it
    // matches this button's conversation the "View" control reads as the active/open thread.
    activeConversationPublicId?: string | null;
    // new optional callback invoked when the user clicks "Discuss"
    onDiscussClicked?: (argumentPublicId: string) => void;
    onViewConversationClicked?: (conversationPublicId: string, argumentPublicId?: string) => void;
    onArgumentUpdated?: (updatedArgument: Partial<ThoughtInConversationDTO>) => void;
    // --- EDITABLE REPLIES (designs/01-now/2026-09-10-editable-replies-design.md §3.6). The CARD owns the
    // edit — it swaps its own body for the composer and records the server's answer — so this row only
    // reports the choice, the same division of labour as mind-changed above. Offered only while
    // `canDelete` holds (the server's "you wrote this and it is live") AND a handler is supplied, so a
    // host with no edit mode never shows an item that does nothing.
    onEditClicked?: () => void;
    // Whether "Edit history" is worth offering: the card knows (a fetched history, a visible marker, or
    // an edit made in this tab); this row does not guess.
    hasEditHistory?: boolean;
    onEditHistoryClicked?: () => void;
    // Fired when the overflow menu OPENS — the moment the owner's card fetches the history, because that
    // read is only worth making when the member is looking at the menu that offers it (the same gating
    // as the block/mute fetch below).
    onOverflowMenuOpened?: () => void;
};

export function ThoughtButtons(props: Props) {
    const apiClient: AjaxThoughtRestControllerApi = ClientFactory.getAjaxThoughtRestControllerApi();

    // Same three routes to a stale prop as on a discussion card, so the same registry — see
    // `ViewerActionRecords`. The vote is spread across four `useState`s (each read individually below),
    // so it seeds through `resolveViewerActionRecord` rather than the hook.
    const seedVoteState = (): DiscussionVoteState => resolveViewerActionRecord("thought-vote", props.argumentPublicId, {
        agreementsCount: props.initialAgreementsCount ?? 0,
        disagreementsCount: props.initialDisagreementsCount ?? 0,
        viewerAgreeing: props.initiallyAgreed ?? false,
        viewerDisagreeing: props.initiallyDisagreed ?? false,
    });

    const [agreementsCount, setAgreementsCount] = useState<number>(() => seedVoteState().agreementsCount);
    const [disagreementsCount, setDisagreementsCount] = useState<number>(() => seedVoteState().disagreementsCount);
    const [isAgreed, setIsAgreed] = useState<boolean>(() => seedVoteState().viewerAgreeing);
    const [isDisagreed, setIsDisagreed] = useState<boolean>(() => seedVoteState().viewerDisagreeing);
    const [isBookmarked, setIsBookmarked] = useViewerActionRecord(
        "thought-bookmark", props.argumentPublicId, props.initiallyBookmarked ?? false,
    );

    const applyVoteState = (state: DiscussionVoteState): void => {
        setAgreementsCount(state.agreementsCount);
        setDisagreementsCount(state.disagreementsCount);
        setIsAgreed(state.viewerAgreeing);
        setIsDisagreed(state.viewerDisagreeing);
    };
    // In-flight guards: block a second vote/bookmark request while the first is still awaiting, so a
    // fast double-click can't read stale closure state and desync the displayed count from the
    // persisted one (mirrors DiscussionVoteButtons.isTogglingAgreement). Two separate flags so a vote
    // in flight never disables the bookmark and vice versa.
    const [isTogglingVote, setIsTogglingVote] = useState<boolean>(false);
    const [isTogglingBookmark, setIsTogglingBookmark] = useState<boolean>(false);
    const [isDeleted, setIsDeleted] = useState<boolean>(Boolean(props.isDeleted));
    const [canDelete, setCanDelete] = useState<boolean>(Boolean(props.canDelete));
    const [canStartConversation, setCanStartConversation] = useState<boolean>(props.canStartConversation ?? !props.isDeleted);
    const [isPinned, setIsPinned] = useViewerActionRecord("thought-pinned", props.argumentPublicId, Boolean(props.pinned));
    // Same in-flight guard shape as the vote/bookmark toggles: a fast double-click must not fire a
    // second write against stale closure state.
    const [isTogglingPin, setIsTogglingPin] = useState<boolean>(false);

    const id = props.argumentPublicId;
    const localizer = props.localizer;
    // On narrow screens the secondary actions (bookmark, share) move into the overflow
    // menu so the row keeps Agree / Disagree / Discuss as the primary, uncluttered actions.
    const isMobile = useIsMobile();
    const accountAreaAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.accountArea, "argument_account_area");
    // QA-10: a visitor who signs in from THIS reply's Agree / Disagree, Save, Report or Endorse comes back
    // to the reply (`/thought/<id>`), not to the top of the post, exactly as the composer's Publish gate
    // does (SV-47 §7.1). The endorse context is handed to `EndorsementControl` as a prop.
    const replyContinuation = buildWriteThoughtContinuation({ parentArgumentPublicId: id });
    const reactAccountAccessDialog = { ...withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.react, "argument_react"), continuation: replyContinuation };
    const reportAccountAccessDialog = { ...withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.report, "argument_report"), continuation: replyContinuation };
    const endorseAccountAccessDialog = { ...withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.react, "argument_endorse"), continuation: replyContinuation };
    const saveAccountAccessDialog = { ...withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.save, "argument_save"), continuation: replyContinuation };
    const safetyAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.safety, "user_safety");

    // Block / mute the author of THIS comment, from the same menu that already offers Report (design
    // 2026-08-10, slice A). Until this shipped a member meeting a harasser in a comment thread had to
    // already know to navigate to `/user/<name>` and open a different menu, which is why the member whose
    // complaint produced this filed two content reports and zero user reports.
    //
    // The fetch is gated on the menu actually being OPEN. A discussion with forty comments across twenty
    // authors would otherwise issue twenty safety-state requests on mount for a menu almost nobody opens;
    // opening one is a deliberate act, so that is when the read is worth making.
    const [safetyMenuOpen, setSafetyMenuOpen] = useState<boolean>(false);
    const safety = useUserSafetyActions({
        username: props.authorUsername,
        localizer: props.localizer,
        enabled: safetyMenuOpen,
    });
    const handleOverflowMenuOpenChange = (open: boolean) => {
        setSafetyMenuOpen(open);
        if (open) {
            props.onOverflowMenuOpened?.();
        }
    };

    React.useEffect(() => {
        setIsDeleted(Boolean(props.isDeleted));
    }, [props.isDeleted]);

    React.useEffect(() => {
        setCanDelete(Boolean(props.canDelete) && !Boolean(props.isDeleted));
    }, [props.canDelete, props.isDeleted]);

    React.useEffect(() => {
        setCanStartConversation(props.canStartConversation ?? !Boolean(props.isDeleted));
    }, [props.canStartConversation, props.isDeleted]);

    // The pin and bookmark resync effects are GONE: `useViewerActionRecord` owns that resync and does
    // it against `record ?? prop`, where these did it blindly against `prop` — which is what let a
    // stale bootstrap payload clobber the viewer's own action without any unmount at all.
    //
    // The vote keeps an explicit effect because it is not hook-backed, and it resyncs from the same
    // `record ?? prop` source the mount seed uses.
    React.useEffect(() => {
        const seeded = seedVoteState();
        setAgreementsCount(seeded.agreementsCount);
        setDisagreementsCount(seeded.disagreementsCount);
        setIsAgreed(seeded.viewerAgreeing);
        setIsDisagreed(seeded.viewerDisagreeing);
    }, [
        props.argumentPublicId,
        props.initialAgreementsCount,
        props.initialDisagreementsCount,
        props.initiallyAgreed,
        props.initiallyDisagreed,
    ]);

    const toggleVote = async (mode: "UPVOTE" | "DOWNVOTE") => {
        if (isTogglingVote) return;
        setIsTogglingVote(true);
        const currentlyActive = mode === "UPVOTE" ? isAgreed : isDisagreed;
        const previousVoteState: DiscussionVoteState = {
            agreementsCount,
            disagreementsCount,
            viewerAgreeing: isAgreed,
            viewerDisagreeing: isDisagreed,
        };
        // The same toggle arithmetic the discussion vote uses, from the same function — including the
        // switch, where both counts move at once. Three hand-written copies of it was two too many.
        // (`UPVOTE`/`DOWNVOTE` is this endpoint's spelling of `AGREE`/`DISAGREE`.)
        const optimisticState = applyVoteToState(previousVoteState, mode === "UPVOTE" ? "AGREE" : "DISAGREE");

        applyVoteState(optimisticState);

        try {
            if (currentlyActive) {
                await apiClient.removeVoteFromThought(id);
            } else {
                await apiClient.voteOnThought(id, mode);
                trackThoughtVote(mode === "UPVOTE" ? "upvote" : "downvote");
            }
            // Recorded only once the write has SETTLED, so the record never holds a vote the server
            // refused. This endpoint returns no state body, so the optimistic prediction is the best
            // truth available — unlike the discussion vote, which prefers the server's answer.
            rememberViewerActionRecord("thought-vote", id, optimisticState);
        } catch (e) {
            applyVoteState(previousVoteState);
            // The revert is recorded too: leaving a record of the failed vote in place would make the
            // next remount render it, which is the defect this registry exists to prevent, inverted.
            rememberViewerActionRecord("thought-vote", id, previousVoteState);
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(localizer("typescript.thought.vote.update_failed"));
        } finally {
            setIsTogglingVote(false);
        }
    };

    const toggleBookmark = async () => {
        if (isTogglingBookmark) return;
        setIsTogglingBookmark(true);
        const currentlyBookmarked = isBookmarked;

        setIsBookmarked(!currentlyBookmarked);

        try {
            if (currentlyBookmarked) {
                await apiClient.unbookmarkThought(id);
                trackBookmarkRemoved("thought");
                toast.success(localizer("typescript.discussion.bookmarks.removed"));
            } else {
                await apiClient.bookmarkThought(id);
                trackBookmarkAdded("thought");
                toast.success(localizer("typescript.discussion.bookmarks.saved"));
            }
        } catch (e) {
            // rollback
            setIsBookmarked(currentlyBookmarked);
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(localizer("typescript.thought.bookmark.update_failed"));
        } finally {
            setIsTogglingBookmark(false);
        }
    };

    // A thought with no resolved public id has NO permalink. The prop is typed `string`, but both
    // call sites pass `displayArgument.publicId!` over a DTO field that is genuinely nullable
    // (`publicId?: string`, backed by a nullable `UUID`) — so a runtime `null` reaches this line and
    // `${null}` stringifies to the literal "null". That produced shareable
    // `/discussion/{slug}/thought/null` URLs that 404 forever once posted somewhere; see
    // `designs/08-audits-and-process/2026-07-24-prod-log-12h-audit-and-root-causes.md` finding 1.
    // `null` here also hides the Share control, so there is nothing to share until the id arrives.
    // Same guard shape as every sibling link builder (ThoughtThread, ThoughtAncestorBreadcrumb,
    // SearchThoughtResultRow, ThoughtCardDisplayer, DiscussionPage).
    const argumentPermalinkPath = id
        ? (props.conversationPublicId
            ? (props.discussionSlug
                ? `/discussion/${props.discussionSlug}/conversation/${props.conversationPublicId}/thought/${id}`
                : `/conversation/${props.conversationPublicId}/thought/${id}`)
            : (props.discussionSlug
                ? thoughtPermalinkHref(props.discussionSlug, id)
                : `/thought/${encodeURIComponent(String(id))}`))
        : null;

    const conversationPath = props.conversationPublicId
        ? (props.discussionSlug
            ? `/discussion/${props.discussionSlug}/conversation/${props.conversationPublicId}`
            : `/conversation/${props.conversationPublicId}`)
        : null;

    const viewConversationHref = props.conversationPublicId
        ? conversationPath
        : null;

    // True when this button's conversation is the one currently open in the discussion rail.
    // Drives the linked highlight (subtle accent + "Viewing" label + aria-pressed) so it is
    // obvious which thread the rail panel is showing.
    const isViewingThisConversation = Boolean(
        props.conversationPublicId
        && props.activeConversationPublicId
        && props.conversationPublicId === props.activeConversationPublicId,
    );

    const handleShareArgument = async () => {
        if (!argumentPermalinkPath) {
            return;
        }
        trackShare("thought", "thought_actions");
        const shareUrlStr = (typeof window !== 'undefined' ? window.location.origin : '') + argumentPermalinkPath;
        // A thought inherits its meaning from its HOST post, so the payload's title is that post's, not
        // this reply's own text and not the "Share thought" UI label this used to send (BL-083, closed
        // 2026-08-07 — until then this was the last surface handing a button label to Android's
        // EXTRA_SUBJECT). The title arrives as a prop from the page; see the design's §3.7a for the
        // path and for why the conversation rail is still un-threaded.
        //
        // ⚠️ Deliberately NO `authorDisplayName`. `entityTitle` here is someone else's post title, so
        // naming the replier beside it would attribute that post to them — the rule is written on
        // `ShareContext` and this is the call site it was written for.
        //
        // Omitted rather than sent blank when there is no title: the field is absent from the context so
        // the chokepoint falls back to the positional argument and `SHARE_INITIATED` never records an
        // empty title.
        const hostPostTitle = props.hostPostTitle?.trim();
        await shareUrl(
            shareUrlStr,
            localizer("typescript.thought.share.title"),
            localizer,
            hostPostTitle
                ? { entityType: "THOUGHT", entityId: props.argumentPublicId, entityTitle: hostPostTitle }
                : { entityType: "THOUGHT", entityId: props.argumentPublicId },
        );
    };

    const handleDiscussClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
        try {
            e?.preventDefault();
            e?.stopPropagation();
        } catch {
        }
        if (props.onDiscussClicked) {
            props.onDiscussClicked(id);
        }
    };

    const handleViewConversationClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
        if (!props.conversationPublicId || !props.onViewConversationClicked) return;
        try {
            e?.preventDefault();
            e?.stopPropagation();
        } catch {
        }
        props.onViewConversationClicked(props.conversationPublicId);
    };

    const handleDeleteArgument = () => {
        showYesNoDialog({
            localizer,
            title: localizer("typescript.thought.delete.confirm.title"),
            message: (
                <div className="space-y-2 text-sm text-text-main/90">
                    <p>{localizer("typescript.thought.delete.confirm.body")}</p>
                    <p>{localizer("typescript.thought.delete.confirm.replies")}</p>
                </div>
            ),
            yesLabel: localizer("typescript.thought.delete.action"),
            yesIcon: Trash2,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            destructive: true,
            // Both halves are named, not just the destructive one: a confirm whose Cancel path has no
            // hook cannot be tested for the thing that actually matters about a confirm — that saying
            // no leaves the thought alone.
            yesPlaywrightId: "thought-delete-confirm",
            noPlaywrightId: "thought-delete-cancel",
            onYes: async () => {
                try {
                    const { data: updatedArgument } = await apiClient.deleteThought(id);
                    setIsDeleted(true);
                    setCanDelete(false);
                    setCanStartConversation(false);
                    props.onArgumentUpdated?.(updatedArgument ?? {});
                    // Undo lives on the toast because the card stays on screen here (contrast the
                    // discussion delete, which navigates away and would destroy its own toast). This
                    // catches the mis-tap; the dashboard's Deleted tab is what catches the regret.
                    toast.success(localizer("typescript.thought.delete.success"), {
                        action: {
                            label: localizer("dashboard.deleted.restore.action"),
                            onClick: () => {
                                void (async () => {
                                    try {
                                        const { data: restored } = await ClientFactory.restoreOwnDeletedThought(id);
                                        // Read back from the returned DTO rather than assuming the
                                        // pre-delete values: the server decides what the viewer may do
                                        // with the restored thought, and its answer can differ from
                                        // what it was before (a tier change, a closed discussion).
                                        setIsDeleted(false);
                                        setCanDelete(Boolean(restored?.canDelete));
                                        setCanStartConversation(Boolean(restored?.canStartConversation));
                                        props.onArgumentUpdated?.(restored ?? {});
                                        toast.success(localizer("dashboard.deleted.restore.thought_success"));
                                    } catch (restoreError) {
                                        logger.error(restoreError);
                                        toast.error(localizer("dashboard.deleted.restore.failed"));
                                    }
                                })();
                            },
                        },
                    });
                } catch (e) {
                    if (isHandledAuthSessionError(e)) {
                        return;
                    }
                    // The YesNoDialog owns the failure message now (single source of truth): it shows
                    // the localized banner and stays open when onYes rejects. Re-throw so it can — but
                    // don't also toast, or the user sees the same error twice.
                    logger.error(e);
                    throw e;
                }
            },
        });
    };

    // Pin/unpin. Deliberately NOT optimistic: pinning changes the list's ORDER, and the server owns
    // that ordering under all seven of the discussion's sortings. Re-sorting locally would be a second,
    // divergent implementation of "pinned first" that disagrees with the server on the next page load,
    // so the successful path defers entirely to `onPinChanged` (which re-fetches page 1).
    const applyPinChange = async (nextPinned: boolean, replacedAnotherPin: boolean) => {
        if (isTogglingPin) return;
        setIsTogglingPin(true);
        try {
            if (nextPinned) {
                await ClientFactory.pinThought(id);
                setIsPinned(true);
                trackThoughtPinned(replacedAnotherPin);
                toast.success(localizer("typescript.thought.pin.success"));
            } else {
                await ClientFactory.unpinThought(id);
                setIsPinned(false);
                toast.success(localizer("typescript.thought.unpin.success"));
            }
            props.onPinChanged?.();
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(localizer("typescript.thought.pin.failed"));
        } finally {
            setIsTogglingPin(false);
        }
    };

    const handlePinClicked = () => {
        // A DIFFERENT thought holds the slot. Confirm before replacing it — following YouTube, and for
        // a concrete reason: the current pin may be on page 4 and entirely out of sight, so a silent
        // replace is an invisible destructive side effect. Skipped when nothing else is pinned, so the
        // common path stays one click.
        const otherPinnedThoughtPublicId = props.pinnedThoughtPublicId && props.pinnedThoughtPublicId !== id
            ? props.pinnedThoughtPublicId
            : null;
        if (!otherPinnedThoughtPublicId) {
            void applyPinChange(true, false);
            return;
        }

        showYesNoDialog({
            localizer,
            title: localizer("typescript.thought.pin.replace.confirm.title"),
            message: (
                <div className="space-y-2 text-sm text-text-main/90">
                    {/* Deliberately does NOT quote the other thought's text: a thought is a full
                        rich-text body and can be arbitrarily long. */}
                    <p>{localizer("typescript.thought.pin.replace.confirm.body")}</p>
                </div>
            ),
            yesLabel: localizer("typescript.thought.pin.action"),
            yesIcon: Pin,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                await applyPinChange(true, true);
            },
        });
    };

    // Accept / unaccept an answer. Server-authoritative, for the same reason the pin above is: the
    // accepted answer floats to the top under all seven orderings and the server owns that ordering. The
    // badge is therefore driven by the RESPONSE (`QuestionStateView`), not by an optimistic local guess —
    // which is also what ClientFactory's contract note for these verbs requires, because the server
    // resolves `ANSWERED` through the answer's visibility and can legitimately disagree with the guess.
    //
    // A 403 here is a legitimate outcome, not a bug to retry around: `canAccept` can have gone stale (the
    // post was archived, the answer deleted). It surfaces as the server's own message.
    const [isTogglingAccept, setIsTogglingAccept] = useState<boolean>(false);
    const applyAcceptChange = async (nextAccepted: boolean) => {
        const discussionPublicId = props.discussionPublicId;
        if (isTogglingAccept || !discussionPublicId) {
            return;
        }
        setIsTogglingAccept(true);
        try {
            const response = nextAccepted
                ? await ClientFactory.acceptQuestionAnswer(discussionPublicId, id)
                : await ClientFactory.unacceptQuestionAnswer(discussionPublicId);
            const view = response.data;
            // Trust the server's own view of which answer is accepted rather than the verb we sent.
            const acceptedNow = view.state === "ANSWERED" && view.acceptedThoughtPublicId === id;
            props.onAcceptedChanged?.(acceptedNow, view);
            toast.success(localizer(nextAccepted
                ? "discussion.question.toast.accepted"
                : "discussion.question.toast.unaccepted"));
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(getSafeToastMessageFromError(e, localizer("discussion.question.error.generic")));
        } finally {
            setIsTogglingAccept(false);
        }
    };

    // isAuthor still gates author-only affordances (e.g. you cannot report your own argument), but it
    // no longer disables replying — authors may reply to their own argument in the threaded model.
    const isAuthor = props.isAuthorTheLoggedInUser;
    // The three question-facet gates, resolved once. `canAccept` is already false for non-askers, so
    // `isQuestionAnswer` is belt-and-braces against a stale projection reaching an ordinary post.
    const isAcceptedAnswer = Boolean(props.isQuestionAnswer) && Boolean(props.accepted);
    const canOfferAccept = Boolean(props.isQuestionAnswer) && Boolean(props.canAccept)
        && Boolean(props.discussionPublicId) && !isDeleted;

    // The mind-changed control. Hidden once the badge is showing: the mark is not retractable in v1
    // (UX §4.4), so a control on an already-marked thought could only ever produce a refusal. Unlike
    // its accept sibling this needs no discussionPublicId — the verb is addressed to the THOUGHT, and
    // the server resolves the post from it precisely so a client cannot name the post it is being
    // authorized against.
    const canOfferMindChanged = Boolean(props.canMarkMindChanged) && !Boolean(props.mindChanged)
        && !isDeleted && Boolean(props.onMarkMindChangedClicked);

    const showConversationControls = props.displayConversationButton !== false;
    const moderationItems: DropdownItem[] = [];

    if (!isDeleted && !isAuthor) {
        moderationItems.push({
            id: "report-argument",
            icon: Flag,
            text: localizer("report.modal.submit_button.entity", localizer("report.entity.thought")),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                showReportEntityDialog(localizer, "argument", id);
            },
            requiresLogin: true,
            accountAccessDialog: reportAccountAccessDialog,
            destructive: true,
        });

        // Directly under Report, and gated the same way. `canOfferSafetyControls` is what suppresses the
        // pair on your own comment and on the support account (D29) — hidden rather than inert, because a
        // control that looks like it acted and did nothing is worse than one that is not there.
        moderationItems.push(...buildUserSafetyDropdownItems(safety, localizer, safetyAccountAccessDialog));
    }

    // Editable replies: Edit, then Edit history, then Delete — the author's three verbs over their own
    // words, in the order of how often they are wanted. In the reply's OWN menu, which is the surface
    // that owns the object (flat-X rule 6).
    if (!isDeleted && canDelete && props.onEditClicked) {
        moderationItems.push({
            id: "edit-thought",
            icon: Pencil,
            text: localizer("thought.menu.edit"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                props.onEditClicked?.();
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
        if (props.hasEditHistory && props.onEditHistoryClicked) {
            moderationItems.push({
                id: "edit-history",
                icon: History,
                text: localizer("thought.history.title"),
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    props.onEditHistoryClicked?.();
                },
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
            });
        }
    }

    if (!isDeleted && canDelete) {
        moderationItems.push({
            id: "delete-argument",
            icon: Trash2,
            text: localizer("typescript.thought.delete.action"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                handleDeleteArgument();
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
            destructive: true,
        });
    }

    // The discussion author's editorial control, FIRST in the overflow menu — ahead of bookmark, share
    // and the moderation actions. A feature buried in a menu nobody opens does nothing, and this is the
    // cheapest discovery we can buy without adding clutter to every card for an action taken at most
    // once per discussion. `THOUGHT_PINNED` measures whether that was enough (design D7).
    //
    // Note the pin item is gated on `canPin` (the DISCUSSION's author) while the UNPIN item is also
    // offered to this thought's own author: someone pinned in order to be ridiculed otherwise has only
    // one exit, deleting their own words. The two conditions are kept separate rather than folded into
    // one flag — sharing one is exactly how the pin path would inherit the wider rule (design D5).
    const authorItems: DropdownItem[] = [];
    // Unaccept lives in the overflow menu, while Accept is a button on the card (Q-D9). That asymmetry is
    // deliberate: accepting is the action the facet exists for and needs to be one tap on any answer,
    // whereas unaccepting is a correction on the single card that already carries the badge — and it is
    // also the ONLY exit from the ANSWERED state, since the post menu offers no verb there.
    if (canOfferAccept && isAcceptedAnswer) {
        authorItems.push({
            id: "unaccept-answer",
            icon: CircleSlash,
            text: localizer("discussion.question.unaccept"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void applyAcceptChange(false);
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }
    if (!isDeleted && isPinned && (props.canPin || isAuthor)) {
        authorItems.push({
            id: "unpin-thought",
            icon: PinOff,
            text: localizer("typescript.thought.unpin.action"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void applyPinChange(false, false);
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    } else if (!isDeleted && !isPinned && props.canPin) {
        authorItems.push({
            id: "pin-thought",
            icon: Pin,
            text: localizer("typescript.thought.pin.action"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                handlePinClicked();
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });
    }

    // On mobile the secondary actions (bookmark, share) are folded into the overflow menu,
    // ahead of any moderation actions, so the inline row stays focused on the primary actions.
    const overflowItems: DropdownItem[] = [];
    overflowItems.push(...authorItems);
    if (isMobile && !isDeleted) {
        overflowItems.push({
            id: "bookmark-argument",
            icon: Bookmark,
            text: localizer("typescript.thought.bookmark"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void toggleBookmark();
            },
            requiresLogin: true,
            accountAccessDialog: saveAccountAccessDialog,
        });
    }
    // Only offered when the thought actually has a permalink (see argumentPermalinkPath above).
    if (isMobile && !isDeleted && argumentPermalinkPath) {
        overflowItems.push({
            id: "share-argument",
            icon: Share,
            text: localizer("typescript.thought.share"),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void handleShareArgument();
            },
            requiresLogin: false,
        });
    }
    overflowItems.push(...moderationItems);

    return (
        // A touch-only gap bump so the enlarged 44px tap targets (see getCtaSizeTouchTargetClass)
        // don't visually collide; the joined vote pair stays one control (ButtonGroup is gapless).
        // `flex-wrap` so the endorsement note composer can take a full-width line under the row
        // (`basis-full`); nothing else in the row is allowed to wrap on its own.
        <div className="flex flex-wrap items-center gap-2 [@media(pointer:coarse)]:gap-3">
            {!isDeleted && !isMobile && (
                <CtaToggle
                    icon={Bookmark}
                    variant={"ghost"}
                    extraClass="follow-argument-button"
                    requiresLogin={true}
                    accountAccessDialog={saveAccountAccessDialog}
                    buttonActive={isBookmarked}
                    interactionEffect="bookmark"
                    onPressedChange={() => toggleBookmark()}
                    disabled={isTogglingBookmark}
                    data-argument-id={id}
                    title={localizer("typescript.thought.bookmark")}
                    size="sm"
                />
            )}


            {!isDeleted && (
                <VoteContainer
                    leftButtonIcon={ArrowBigUp}
                    leftButtonTitle={props.localizer("typescript.thought.upvote")}
                    leftButtonCount={agreementsCount}
                    leftButtonActive={isAgreed}
                    leftButtonExtraClass="upvote-thought-button"
                    leftInteractionEffect="agree"
                    leftCountFeedback="pulse"
                    rightButtonIcon={ArrowBigDown}
                    rightButtonTitle={props.localizer("typescript.thought.downvote")}
                    rightButtonCount={disagreementsCount}
                    rightButtonActive={isDisagreed}
                    rightButtonExtraClass="downvote-thought-button"
                    rightInteractionEffect="disagree"
                    rightCountFeedback="pulse"
                    requiresLogin={true}
                    accountAccessDialog={reactAccountAccessDialog}
                    disabled={isTogglingVote}
                    localizer={localizer}
                    displayTextOnMobile={false}
                    onLeftClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                        try {
                            (ev as React.MouseEvent)?.preventDefault();
                            (ev as React.MouseEvent)?.stopPropagation();
                        } catch {
                        }
                        await toggleVote("UPVOTE");
                    }}
                    onRightClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                        try {
                            (ev as React.MouseEvent)?.preventDefault();
                            (ev as React.MouseEvent)?.stopPropagation();
                        } catch {
                        }
                        await toggleVote("DOWNVOTE");
                    }}
                    size="sm"
                />
            )}

            {/* Share button placed between votes and conversation controls (desktop only;
                folded into the overflow menu on mobile). */}
            {!isDeleted && !isMobile && argumentPermalinkPath && (
                <CtaButton
                    icon={Share}
                    text={localizer("typescript.thought.share")}
                    variant={"ghost"}
                    requiresLogin={false}
                    displayTextOnMobile={false}

                    extraClass="share-argument-button"
                    playwrightId="thought-share-button"
                    title={localizer("typescript.thought.share")}
                    size="sm"
                    onClick={async (e?: React.MouseEvent<HTMLButtonElement>) => {
                        try {
                            e?.preventDefault();
                            e?.stopPropagation();
                        } catch {
                            // ignore
                        }

                        await handleShareArgument();
                    }}
                />
            )}

            {showConversationControls && viewConversationHref && (
                <CtaButton
                    icon={Eye}
                    text={isViewingThisConversation ? localizer("typescript.thought.viewing") : localizer("typescript.thought.view")}
                    variant={"outline"}
                    roundedLeft={true}
                    displayTextOnMobile={false}

                    roundedRight={true}
                    requiresLogin={false}
                    href={viewConversationHref}
                    buttonActive={isViewingThisConversation}
                    activeClass="border-primary/60 bg-primary/10 text-primary"
                    extraClass="view-conversation-argument-button"
                    playwrightId="thought-view-conversation-button"
                    title={isViewingThisConversation ? localizer("typescript.thought.viewing") : localizer("typescript.thought.view")}
                    size="sm"
                    aria-pressed={isViewingThisConversation}
                    data-conversation-public-id={props.conversationPublicId ?? undefined}
                    onClick={props.onViewConversationClicked ? (e) => handleViewConversationClick(e as React.MouseEvent<HTMLButtonElement>) : undefined}
                />
            )}

            {/* Only show the interactive "Discuss" button when there is no conversation link and conversation controls are allowed.
                Child arguments (with a conversation) will therefore expose the link above instead. */}
            {showConversationControls && !viewConversationHref && !isDeleted && canStartConversation && (
                <CtaButton
                    icon={MessageCircle}
                    text={localizer("typescript.thought.reply")}
                    variant={"secondary"}
                    // SV-47: gate at Publish, not before writing — see DiscussionThoughtsDisplayer.
                    requiresLogin={false}
                    displayTextOnMobile={false}


                    roundedRight={true}
                    roundedLeft={true}
                    // DISCUSSION-50 — the boundary, NOT a louder fill. Reply is the row's primary
                    // action and on a phone it is a wordless disc, so its silhouette is the only thing
                    // marking it as a control; sampled from the 2026-09-02 frames it measured 1.15:1
                    // (light) and 1.07:1 (dark) against the page, under WCAG 1.4.11's 3:1, while the
                    // deliberately-quiet Endorse pill beside it carried 3.88:1 / 4.50:1.
                    //
                    // The root is that the two spellings of `secondary` disagree: components/ui/
                    // button.tsx gave the variant `border border-control-boundary` on 2026-08-23 for
                    // exactly this reason, and ctaShared.ts's `getCtaVariantClasses("secondary")` still
                    // returns `border-transparent`, which wins because getCtaClasses merges it after
                    // the base variant. ctaShared.ts is a frozen shared primitive for wave 17 (§2.3 of
                    // the pass plan), so this is the call-site fix: `extraClass` is merged LAST, so
                    // twMerge lets `border-control-boundary` beat `border-transparent`, and the width
                    // already comes from `border` in ctaBaseClass so the h-8 box does not grow.
                    // `--control-boundary` is the token this repo already settled on for a control's
                    // own edge (3.23-4.63:1 on every surface a control sits on, both themes); a new
                    // colour here would be a fifth answer to a settled question.
                    extraClass="start-conversation-argument-button border-control-boundary"
                    playwrightId="thought-discuss-button"
                    title={localizer("typescript.thought.reply")}
                    size="sm"
                    // Authors may reply to their own argument (threaded-comments model): no self-reply gate.
                    onClick={(e) => {
                        // either call provided callback or navigate
                        handleDiscussClick(e as React.MouseEvent<HTMLButtonElement>);
                    }}
                />
            )}

            {/* Accept (Q-D9): on the card, not buried in the overflow menu, because marking the answer
                that helped is the entire point of the facet and a menu nobody opens would strand every
                question in OPEN. Only the asker ever sees it — `canAccept` is their permission, not this
                answer's property — so it adds no clutter for anyone else. Text is hidden on mobile like
                every sibling here (this row is ~300px of 44px targets on a ~390px phone), with the
                meaning carried by the aria-label. The pill inherits the 44px floor from `size="sm"`. */}
            {canOfferAccept && !isAcceptedAnswer && (
                <CtaButton
                    icon={Check}
                    text={localizer("discussion.question.accept")}
                    variant={"outline"}
                    roundedLeft={true}
                    roundedRight={true}
                    requiresLogin={true}
                    accountAccessDialog={accountAreaAccountAccessDialog}
                    displayTextOnMobile={false}
                    disabled={isTogglingAccept}
                    extraClass="accept-answer-button"
                    playwrightId="thought-accept-answer-button"
                    title={localizer("discussion.question.accept.aria")}
                    aria-label={localizer("discussion.question.accept.aria")}
                    size="sm"
                    onClick={(e) => {
                        try {
                            e?.preventDefault();
                            e?.stopPropagation();
                        } catch {
                        }
                        void applyAcceptChange(true);
                    }}
                />
            )}

            {/* CHANGED MY MIND, beside Accept and for the same reason (Q-D9 restated): marking the
                thought that moved you is the entire point of the mechanic, and a menu nobody opens
                would strand every post. Only the post's author ever sees it — `canMarkMindChanged` is
                their permission, not this thought's property — so it adds no clutter for anyone else.
                Text hidden on mobile like every sibling here, with the meaning carried by the
                aria-label; the pill inherits the 44px floor from size="sm". */}
            {canOfferMindChanged && (
                <CtaButton
                    icon={Sparkles}
                    text={localizer("discussion.mind_changed.action")}
                    variant={"outline"}
                    roundedLeft={true}
                    roundedRight={true}
                    requiresLogin={true}
                    accountAccessDialog={accountAreaAccountAccessDialog}
                    displayTextOnMobile={false}
                    extraClass="mind-changed-button"
                    playwrightId="discussion-mind-changed-button"
                    title={localizer("discussion.mind_changed.action")}
                    aria-label={localizer("discussion.mind_changed.action")}
                    size="sm"
                    onClick={(e) => {
                        try {
                            e?.preventDefault();
                            e?.stopPropagation();
                        } catch {
                        }
                        props.onMarkMindChangedClicked?.();
                    }}
                />
            )}

            {/* ENDORSE (design §9.1): a quiet secondary control, never Reply's peer. Offered to every
                reader who is not this thought's author, on every host of this row; an anonymous reader
                reaches the account-access dialog exactly as Report does (§9.4). The control, the chip
                and the note composer are one component with one record, so the count the chip draws
                and the kind the pill reads cannot drift. Hidden on a tombstone and on your own thought
                (E12 — also a database CHECK, so this is only the legible half). */}
            {!isDeleted && !isAuthor && id ? (
                <EndorsementControl
                    thoughtPublicId={id}
                    localizer={localizer}
                    initialSummary={props.endorsements}
                    accountAccessDialog={endorseAccountAccessDialog}
                />
            ) : null}

            {overflowItems.length > 0 && (
                <CtaButtonDropdown
                    icon={MoreHorizontal}
                    // Never filed as its own row, found by the tree sweep: one per thought, so on a
                    // busy discussion this is the most repeated unnamed control in the app. Existing
                    // key — a new label is a 24-locale bill (AGENTS.md rule 11).
                    ariaLabel={localizer("typescript.discussion.preview.publish_settings.more_options")}
                    playwrightId="thought-more-options-button"
                    variant="ghost"
                    size="sm"
                    extraClass="thought-more-options-button"
                    dropdownItems={overflowItems}
                    onOpenChange={handleOverflowMenuOpenChange}
                />
            )}

        </div>
    );
}
